import React, { useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import {
  Alert,
  Box,
  Button,
  Card,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Typography
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import PdfGenerator from 'src/ui-component/PdfGenerator';
import FormHeaderGreeting from 'src/components/Greeting/FormHeaderGreeting';
import Breadcrumb from 'src/ui-component/Breadcrumb';
import LoadingOverlay from 'src/components/common/LoadingOverlay';
import { RootState } from 'src/redux-store/appStore';
import LinearStepper from 'src/ui-component/Stepper';
import GeneralInformationForm from './GeneralInformationForm';
import ApplicationDetailsForm from './ApplicationDetailsForm';
import UploadDocumentsForm from './UploadDocumentsForm';
import ApplicationFee from 'src/ui-component/ApplicationFee';
import {
  resetFormData,
  updateAppId,
  updateFormData
} from 'src/redux-store/slices/constructionFormSlice';
import ViewApplication from 'src/ui-component/ViewApplication';
import { httpRequest } from 'src/services/httpRequest';
import { FieldsSchema } from './schema';
import { handleError } from 'src/redux-store/slices/errorSlice';
import { useNavigate, useParams } from 'react-router-dom';
import TechnicalSections from './TechnicalSections';
import {
  PaymentApplicationDetails,
  PaymentSummary
} from 'src/pages/Payment/types';
import { CertificationTerms } from './CertificationInLieuOfOath';

const NewApplication: React.FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { townshipId, permit } = useParams();
  const [activeStep, setActiveStep] = useState(0);
  const [taxRate, setTaxRate] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(false);
  const [applicationId, setApplicationId] = useState<number>(0);
  const [termsAccepted, setTermsAccepted] = useState(false);

  const userData = useSelector((store: RootState) => store.user.userDetails);
  const formData = useSelector(
    (state: RootState) => state.constructionFormSlice.formData
  );
  const AppId = useSelector(
    (state: RootState) => state.constructionFormSlice.AppId
  );
  const [feeDetails, setFeeDetails] = useState({
    applicationFee: 0,
    inspectionFee: 0
  });

  const handleTermsAccepted = (accepted: boolean) => {
    setTermsAccepted(accepted);
  };

  // Create refs for each form
  const formRefs = {
    generalInfo: useRef(null),
    propertyDetails: useRef(null),
    electricalSubcode: useRef(null),
    uploadDocuments: useRef(null)
  };

  useEffect(() => {
    return () => {
      dispatch(resetFormData());
    };
  }, [dispatch]);

  const getCurrentFormRef = () => {
    switch (activeStep) {
      case 0:
        return formRefs.generalInfo;
      case 1:
        return formRefs.propertyDetails;
      case 2:
        return formRefs.electricalSubcode;
      case 3:
        return formRefs.uploadDocuments;
      default:
        return null;
    }
  };

  const handleStepSubmit = async () => {
    const currentFormRef = getCurrentFormRef();
    if (currentFormRef?.current) {
      try {
        await currentFormRef.current.submitForm();
        const errors = await currentFormRef.current.validateForm();
        return Object.keys(errors).length === 0;
      } catch (error) {
        console.error('Form validation error:', error);
        return false;
      }
    }
    return true; // If no form ref (e.g., Summary step), allow proceeding
  };

  const handleInsertApplication = async (data: any, type = '') => {
    setLoading(true);
    const response = await httpRequest('POST', `/construction/insert`, data);
    if (response?.status === 'success') {
      if (type) return true;
      toast.success(`Application created successfully.`);
      setApplicationId(response?.data?.ApplicationID);
      dispatch(
        updateAppId({ ['applicationId']: response?.data?.ApplicationID })
      );
      setLoading(false);
    } else {
      setLoading(false);
      toast.error(response?.message);
      dispatch(handleError(response?.message));
    }
  };

  const handleProceedToPay = async (data: any) => {
    const response = await httpRequest(
      'PUT',
      `/construction/update/${applicationId}`,
      data
    );
    if (response?.status === 'success') {
      toast.success('Application Updated successfully.');
      const paymentSummary: PaymentSummary = {
        fees: Object.fromEntries(
          Object.entries(feeDetails).filter(([, value]) => value > 0)
        ),
        ...(taxRate > 0 ? { taxRate } : {})
      };

      const paymentApplicationDetails: PaymentApplicationDetails = {
        applicationDetails: {
          userName: `${userData?.FirstName} ${userData?.LastName}`,
          applicationNumber: null,
          applicationId: response?.data?.ApplicationID || applicationId,
          applicationStatus: 'Draft',
          statusPayload: 'In Review L1',
          permitPrefix: 'Construction'
        }
      };
      navigate(`/payment-methods`, {
        state: { paymentSummary, paymentApplicationDetails }
      });
    } else if (response?.type === 'NETWORK_ERROR') {
    } else {
      toast.error(response?.message);
      dispatch(handleError(response?.message));
    }
  };

  const handleUpdateApplication = async (data: any, type = '') => {
    setLoading(true);
    const response = await httpRequest(
      'PUT',
      `/construction/update/${applicationId}`,
      data
    );
    if (response?.status === 'success') {
      if (type) return true;
      toast.success(`Application Updated successfully.`);
      setLoading(false);
      navigate(
        `/${townshipId}/Building or Construction Department/${permit}/${'draft-application'}`
      );
    } else {
      setLoading(false);
      toast.error(response?.message);
      dispatch(handleError(response?.message));
    }
  };

  // Add these state variables to your component
  const [isChecking, setIsChecking] = useState(false);
  const [pendingForms, setPendingForms] = useState([]);
  const [showPendingFormsDialog, setShowPendingFormsDialog] = useState(false);

  // Modified handleButtonAction function
  const handleButtonAction = async (type: string) => {
    switch (type) {
      case 'Next':
        // eslint-disable-next-line no-case-declarations
        const isValid = await handleStepSubmit();
        if (isValid) {
          setActiveStep((prev) => prev + 1);
        }
        break;
      case 'Back':
        setActiveStep((prev) => prev - 1);
        break;
      case 'Proceed to Payment':
        // Update the button state to show checking status
        setIsChecking(true);

        // Check UCC form status
        try {
          const statusResponse = await httpRequest(
            'GET',
            `/construction/${applicationId}/check_ucc_form_status`
          );

          if (statusResponse?.status === 'success') {
            // No forms shared or all completed
            if (
              statusResponse.message === 'No UCC forms found' ||
              (Array.isArray(statusResponse.data) &&
                statusResponse.data.length === 0)
            ) {
              // Proceed with payment flow - no shared forms to block
              handleProceedToPay(formData);
            }
            // Check if there are any pending forms
            else {
              const formData1 = statusResponse.data;
              let hasPendingForms = false;
              const pendingFormDetails = [];

              // Check each form type for pending forms
              Object.keys(formData1).forEach((formType) => {
                if (formData1[formType] && formData1[formType].length > 0) {
                  const currentForm = formData1[formType][0];
                  if (currentForm.Pending && currentForm.Pending > 0) {
                    hasPendingForms = true;
                    pendingFormDetails.push({
                      formType: formType,
                      pendingCount: currentForm.Pending,
                      items: currentForm.Items.filter((item) => !item.IsFilled)
                    });
                  }
                }
              });

              if (hasPendingForms) {
                // Show error dialog with pending form details
                setPendingForms(pendingFormDetails);
                setShowPendingFormsDialog(true);
              } else {
                // All forms completed, proceed with payment
                handleProceedToPay(formData);
              }
            }
          } else {
            // Error checking form status
            toast.error('Failed to verify form status. Please try again.');
          }
        } catch (error) {
          console.error('Error checking UCC form status:', error);
          toast.error('Failed to verify form status. Please try again.');
        } finally {
          setIsChecking(false);
        }
        break;
      case 'Update':
        handleUpdateApplication(formData);
        break;
      default:
        break;
    }
  };

  const steps = [
    {
      label: 'General Information',
      Component: (
        <>
          <GeneralInformationForm
            ref={formRefs.generalInfo}
            formData={formData}
            handleSubmit={() => {
              dispatch(updateFormData({ ['DocumentFileUpload']: [] }));
            }}
          />
          <ApplicationFee
            setTaxRate={setTaxRate}
            setFeeDetails={setFeeDetails}
            feeDetails={feeDetails}
          />
        </>
      )
    },
    {
      label: 'Property Details',
      Component: (
        <ApplicationDetailsForm
          ref={formRefs.propertyDetails}
          formData={formData}
          handleSubmit={() => {
            if (!AppId)
              setTimeout(() => {
                handleInsertApplication(formData);
              }, 100);
          }}
        />
      )
    },
    {
      label: 'Technical Sections',
      Component: <TechnicalSections applicationId={AppId || applicationId} />
    },
    {
      label: 'Upload Documents',
      Component: (
        <UploadDocumentsForm
          formData={formData}
          ApplicationId={applicationId}
        />
      )
    },
    {
      label: 'Summary',
      Component: (
        <>
          <ViewApplication StaticDetails={formData} />
          <Box sx={{ margin: 'auto', maxWidth: '1400px', p: { md: 3, xs: 1 } }}>
            <CertificationTerms
              role={formData?.ApplicantType}
              onAccept={handleTermsAccepted}
            />
          </Box>
        </>
      )
    }
  ];

  return loading ? (
    <LoadingOverlay />
  ) : (
    <Container sx={{ mt: 2 }} className="main-table-container">
      <Breadcrumb PermitName={'Construction'} PageTitle={'New Application'} />

      <FormHeaderGreeting
        name={`${userData?.FirstName} ${userData?.LastName}`}
        applicationType={'new'}
      />

      <Card>
        <Box
          m={3}
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}
        >
          <Typography variant="h4">New Application</Typography>
          <PdfGenerator
            PermitName={'Construction'}
            allFields={FieldsSchema(formData)}
          />
        </Box>

        <Box m={3}>
          <LinearStepper
            activeStep={activeStep}
            steps={steps}
            stepperButton={[
              {
                label: 'Back',
                function: handleButtonAction
              },
              {
                label: 'Next',
                function: handleButtonAction
              }
            ]}
            finalStepButtons={[
              {
                label: 'Update',
                function: handleButtonAction
              },
              {
                label: isChecking ? 'Checking Status...' : 'Proceed to Payment',
                function: handleButtonAction,
                disabled: !termsAccepted
              }
            ]}
          />

          {/* Add the dialog to show pending forms */}
          <Dialog
            open={showPendingFormsDialog}
            onClose={() => setShowPendingFormsDialog(false)}
            maxWidth="md"
            fullWidth
          >
            <DialogTitle>
              <Typography variant="h4" color="error">
                Cannot Proceed to Payment
              </Typography>
            </DialogTitle>
            <Divider />
            <DialogContent>
              <Box sx={{ mb: 2 }}>
                <Alert severity="info" sx={{ mt: 2 }}>
                  You must wait until all shared forms are completed before
                  proceeding to payment. You can view the status of each form in
                  the UCC Forms section.
                </Alert>
                <br />
                <Typography variant="body1">
                  You have pending UCC forms that need to be completed before
                  proceeding to payment:
                </Typography>
              </Box>

              {pendingForms.map((form, index) => (
                <Box
                  key={index}
                  sx={{ mb: 3, p: 2, bgcolor: '#fff4e5', borderRadius: 1 }}
                >
                  <Typography fontWeight="bold">
                    {form.formType} Form - {form.pendingCount} pending
                  </Typography>

                  <Box sx={{ mt: 1 }}>
                    <Typography variant="subtitle2">Pending for:</Typography>
                    <ul style={{ marginTop: 4, paddingLeft: 20 }}>
                      {form.items.map((item, idx) => (
                        <li key={idx}>
                          <Typography variant="body2">
                            {item.ProfessionalName} ({item.ProfessionalEmail})
                          </Typography>
                        </li>
                      ))}
                    </ul>
                  </Box>
                </Box>
              ))}
            </DialogContent>
            <DialogActions>
              <Button
                onClick={() => setShowPendingFormsDialog(false)}
                variant="contained"
              >
                OK, I'll Check Later
              </Button>
            </DialogActions>
          </Dialog>
        </Box>
      </Card>
    </Container>
  );
};

export default NewApplication;
